import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { ValueObject } from '@core/models/value-object.model';
import { FormModel, TypedFormGroup, asFormControl } from '@feature/forms/form-model';
import { emailMaxLength, mediumTextMaxLength, shortTextMaxLength, WaiValidators } from '@feature/forms/wai-validators';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';

import { FormOption } from '../form-option';
import { FileObject } from '@core/models/file-object.model';
import { FileChangeEvent } from '../form-file/form-file.component';
import { DownloaderService } from '@core/downloader/downloader.service';
import { saveAs } from 'file-saver';
import { ContactCopy } from '@core/models/contacts-copy.model';
import { normalizeDate } from '@core/utils';
import { getCurrentDate } from '@shared/utils/date.utils';
@UntilDestroy()
@Component({
    selector: 'wai-form-contact',
    templateUrl: './form-contact.component.html',
    styleUrls: ['./form-contact.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FormContactComponent,
            multi: true,
        },
    ],
    standalone: false
})
export class FormContactComponent implements ControlValueAccessor, OnInit {
  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;

  public form: TypedFormGroup<ContactCopy>;
  @Input() public salutations: FormOption[] = null;

  public asFormControl = asFormControl;
  @Input() public titles: FormOption[] = null;
  @Input() public type: ContactCopy['type'] = null;
  public accept: string = ['image/*', '.pdf'].join(',');
  private _value: ContactCopy = null;

  public currencies: FormOption[] = [
    { name: 'USD', value: 'USD' },
    { name: 'EUR', value: 'EUR' },
    { name: 'GBP', value: 'GBP' },
    { name: 'CHF', value: 'CHF' },
  ];

  @Input()
  public set value(val: ContactCopy) {
    this._value = val;

    if (this.onChange) {
      this.onChange(val);
    }
  }

  public get value(): ContactCopy {
    return this._value;
  }

  public onChange: (value: ContactCopy) => void;
  public onTouched: (value: ContactCopy) => void;
  constructor(private downloader: DownloaderService) {}

  public get today(): Date {
    return getCurrentDate();
  }

  public ngOnInit(): void {
    const controls: Record<keyof ContactCopy, FormControl> = {
      id: new FormControl(),
      uid: new FormControl(),
      address: new FormControl(),
      email: new FormControl(),
      firstName: new FormControl(),
      isPep: new FormControl(),
      lastName: new FormControl(),
      phone: new FormControl(),
      salutation: new FormControl(),
      title: new FormControl(),
      type: new FormControl(this.type),
      signedDeclaration: new FormControl(),
      idCardScan: new FormControl(),
      dateOfBirth: new FormControl(null, [WaiValidators.dateOfBirth]),
      percentageInCompany: new FormControl(),
    };

    this.form = new FormGroup(controls) as TypedFormGroup<ContactCopy>;

    this.form.controls.address.setValidators([
      Validators.maxLength(mediumTextMaxLength),
      WaiValidators.nonEmpty,
      Validators.required,
    ]);
    this.form.controls.email.setValidators([
      WaiValidators.email,
      Validators.maxLength(emailMaxLength),
      WaiValidators.nonEmpty,
      Validators.required,
    ]);
    this.form.controls.firstName.setValidators([
      Validators.maxLength(shortTextMaxLength),
      WaiValidators.nonEmpty,
      Validators.required,
    ]);
    this.form.controls.lastName.setValidators([
      Validators.maxLength(mediumTextMaxLength),
      WaiValidators.nonEmpty,
      Validators.required,
    ]);
    this.form.controls.phone.setValidators([
      Validators.maxLength(shortTextMaxLength),
      WaiValidators.e164Phone,
      WaiValidators.nonEmpty,
      Validators.required,
    ]);
    this.form.controls.salutation.setValidators([WaiValidators.nonEmpty, Validators.required]);
    this.form.controls.title.setValidators([WaiValidators.nonEmpty]);

    // Add validators for beneficial owner fields
    if (this.type === 'benef-owner') {
      this.form.controls.dateOfBirth.setValidators([Validators.required, WaiValidators.dateOfBirth]);
      this.form.controls.percentageInCompany.setValidators([
        WaiValidators.beneficialOwnerPercentage,
        Validators.required,
      ]);
    }

    // Activate all validators - required after calling setValidators() on existing FormControls
    // When setValidators() is called on existing FormControls after FormGroup creation,
    // updateValueAndValidity() must be called to activate the validators
    this.form.controls.address.updateValueAndValidity();
    this.form.controls.email.updateValueAndValidity();
    this.form.controls.firstName.updateValueAndValidity();
    this.form.controls.lastName.updateValueAndValidity();
    this.form.controls.phone.updateValueAndValidity();
    this.form.controls.salutation.updateValueAndValidity();
    this.form.controls.title.updateValueAndValidity();
    if (this.type === 'benef-owner') {
      this.form.controls.dateOfBirth.updateValueAndValidity();
      this.form.controls.percentageInCompany.updateValueAndValidity();
    }

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe((val: FormModel<ContactCopy>) => {

      const salutation: FormOption = this.salutations?.find((v) => v.value === val.salutation);
      const title: FormOption = this.titles?.find((v) => v.value === val.title);

      // Mark phone as touched if it has a value and is invalid
      // This ensures E.164 validation errors display immediately to the user
      if (val.phone && this.form.controls.phone.invalid && !this.form.controls.phone.touched) {
        this.form.controls.phone.markAsTouched();
      }

      const normalizedVal = { ...val };
      if (normalizedVal.dateOfBirth) {
        normalizedVal.dateOfBirth = normalizeDate(normalizedVal.dateOfBirth);
      }

      this.value = ContactCopy.create({
        ...normalizedVal,
        salutation: ValueObject.create({
          codename: val.salutation,
          name: salutation?.name ?? '',
        }),
        title: ValueObject.create({
          codename: val.title,
          name: title?.name ?? '',
        }),
      });
    });
  }

  public writeValue(value: ContactCopy): void {
    if (value) {
      this.form.patchValue({
        ...value,
        salutation: value.salutation.codename,
        title: value.title.codename,
      } as FormModel<ContactCopy>);

      // Mark phone as touched so validation errors display immediately if invalid
      // This ensures users see the '+' prefix requirement on load for edit flows
      if (this.form.controls.phone.invalid) {
        this.form.controls.phone.markAsTouched();
      }
    }
  }

  public registerOnChange(fn: () => {}): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  // on file change:
  public onFileChange(event: FileChangeEvent, controlName: string, form): void {
    let formData = new FormData();

    formData.append(controlName, event.file);
    event.formFileComponent.state = 'uploading';

    if (event.file) {
      event.file.status = 'uploaded';
      form.get(controlName).setValue(event.file);
      event.formFileComponent.state = 'uploaded';

      form.markAsPristine();
    } else {
      event.formFileComponent.state = 'empty';
      form.get(controlName).setValue(null);
      form.markAsPristine();
    }
  }

  public onFileOpen(file: FileObject): void {
    this.downloader.download(file.url).subscribe((blob) => saveAs(blob, file.name));
  }
}
